import {
  CellWithExpandedToggleProps,
  ColumnWithSorting,
  EmptyBlockContent,
  Loading,
  Table,
  useParentSizeContext,
  useTableColumnWidths,
} from '@tradingblock/components';
import { ApplicationErrorsRequest, GroupedApplicationError } from '@tradingblock/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatcher } from '../../../../data/global/hooks';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { Cell } from 'react-table';
import { getLocalDateFromTimezone } from '@tradingblock/api';
import { PositionDropdown } from '../../../Positions/components/PositionDropdown';
import ApplicationErrorsSubRow from './ApplicationErrorsSubRow';
import _ from 'lodash';
import { useBlockTable } from '../../../shared/table/useBlockTableActions';
import { UiApiClient } from '../../../../context/Storage';
import useSWR from 'swr';
import { ApplicationErrorsFilters } from './ApplicationErrorsFilters';

export const buildApplicationErrorsRequestURL = (request: ApplicationErrorsRequest) => {
  const baseURL = 'admin/v1/db/applications/errors';
  const { search, page, pageSize, sortBy, repId, sortByOrder, startDate, endDate } = request;
  let params;

  const startDateUTC = startDate ? startDate.toISOString() : undefined;
  const endDateUTC = endDate ? endDate.toISOString() : undefined;

  if (repId) {
    params = {
      search,
      repId,
      page,
      pageSize,
      sortBy,
      sortByOrder,
      startDate: startDateUTC,
      endDate: endDateUTC,
    };
  } else {
    params = {
      search,
      page,
      pageSize,
      sortBy,
      sortByOrder,
      startDate: startDateUTC,
      endDate: endDateUTC,
    };
  }

  if (!search) {
    delete params.search;
  }

  if (!repId) {
    delete params.repId;
  }

  if (!startDate) {
    delete params.startDate;
  }

  if (!endDate) {
    delete params.endDate;
  }

  return `${baseURL}?${_.map(params, (value, key) => `${key}=${value}`).join('&')}`;
};

const AdminApplicationErrorsColumns: ColumnWithSorting<GroupedApplicationError>[] = [
  {
    Header: 'Error',
    accessor: v => v.message,
    id: 'message',
    Cell: ({ row }: CellWithExpandedToggleProps<GroupedApplicationError>) => {
      return (
        <span {...row.getToggleRowExpandedProps()}>
          <PositionDropdown>{row.original.message}</PositionDropdown>
        </span>
      );
    },
  },
  {
    Header: 'Count',
    accessor: v => v.count,
    id: 'count',
    width: 0.5,
  },
  {
    Header: 'Most Recent Occurrence',
    accessor: v => v.mostRecent,
    id: 'mostRecent',
    Cell: ({ row }: Cell<any>) => {
      return <span> {getLocalDateFromTimezone(new Date(row.original.mostRecent)).format('MM/DD/YY h:mm:ss A')}</span>;
    },
  },
  {
    Header: 'Username',
    accessor: v => v.ownerUsername,
    id: 'ownerUsername',
  },
];

export const ApplicationErrorsTable = () => {
  const { dispatch } = useDispatcher();

  const [pageSize, setPageSize] = useState<number | undefined>(10);
  const [page, setPage] = useState<number | undefined>(0);
  const [sortBy, setSortBy] = useState<string | undefined>('mostRecent');
  const [sortByOrder, setSortByOrder] = useState<string | undefined>('desc');
  const [total, setTotal] = useState<number | undefined>(0);
  const [hasData, setHasData] = useState<boolean>(false);
  const [timeFrame, setTimeframe] = useState<{ startDate: Date | undefined; endDate: Date | undefined }>({
    startDate: undefined,
    endDate: undefined,
  });
  const [search, setSearch] = useState<string>('');

  const onSearchSubmit = useCallback((search: string) => {
    setSearch(search);
  }, []);

  const onTimeframeChange = useCallback((startDate: Date | undefined, endDate: Date | undefined) => {
    if (_.isNil(endDate)) {
      setTimeframe({ startDate: startDate, endDate: undefined });
    } else {
      setTimeframe({ startDate, endDate });
    }
  }, []);

  const profile = useStateSelector(s => s.account.profile);
  const visibleRepIds =
    profile && profile.current && profile.current.visibleRepIds ? profile.current.visibleRepIds : [];

  const state = useStateSelector(s => s);

  const provider = UiApiClient(state, dispatch);

  const applicationErrorFetcher = useCallback(
    async (arg: any) => {
      if (provider) {
        try {
          const requestUrl = buildApplicationErrorsRequestURL({
            search: arg.search,
            page: arg.page,
            pageSize: arg.pageSize,
            sortBy: arg.sortBy,
            sortByOrder: arg.sortByOrder,
            repId: arg.repId,
            startDate: timeFrame.startDate,
            endDate: timeFrame.endDate,
          });
          return provider.get<any>(requestUrl);
        } catch (error) {
          console.error('Error fetching application errors', error);
          return error;
        }
      }
    },
    [provider, timeFrame]
  );

  // useSWR
  const { data: applicationErrors, isLoading: applicationErrorsIsLoading, error: applicationErrorsError } = useSWR(
    {
      search,
      page,
      pageSize,
      sortBy,
      sortByOrder,
      repId: visibleRepIds,
      timeFrame: timeFrame.endDate,
    },
    provider ? applicationErrorFetcher : null
  );

  useEffect(() => {
    if (applicationErrors) {
      setTotal(applicationErrors.total);
      setHasData(true);
    }

    if (applicationErrorsError) {
      setHasData(false);
    }

    if (applicationErrorsIsLoading) {
      setHasData(false);
    }

    if (!applicationErrorsIsLoading && !applicationErrorsError && applicationErrors) {
      setHasData(true);
    }
  }, [applicationErrors, applicationErrorsIsLoading, applicationErrorsError]);

  const getRowKey = useCallback((data: GroupedApplicationError) => data.message, []);

  const renderSubRow = useCallback(
    (row: any) => {
      return <ApplicationErrorsSubRow subRows={row.original.errorInfo} />;
    },
    [applicationErrors]
  );

  const onPage = useCallback((page: number) => {
    setPage(page);
  }, []);

  const onPageSize = useCallback((pageSize: number) => {
    setPageSize(pageSize);
  }, []);

  const pageCount = useMemo(() => {
    if (pageSize) {
      return Math.ceil((total || 0) / pageSize);
    }
    return 0;
  }, [pageSize, total]);

  const getCurrentSortBy = useCallback(
    (newDesc?: boolean) => {
      if (sortBy) {
        return { id: sortBy, desc: !_.isUndefined(newDesc) ? newDesc : sortByOrder === 'desc' };
      }
      return undefined;
    },
    [sortBy, sortByOrder]
  );

  const initialState = useMemo(() => {
    const currentSortBy = getCurrentSortBy();
    return {
      sortBy: currentSortBy ? [currentSortBy] : [],
      pageSize: _.isUndefined(pageSize) ? 10 : pageSize,
      pageIndex: page || 0,
      columnOrder: [],
    };
  }, [page, pageSize, getCurrentSortBy]);

  const onSort = useCallback(
    (sort: any[]) => {
      const sortVal = _.first(sort) || getCurrentSortBy(sortByOrder === 'asc');
      if (sortVal) {
        const { id, desc } = sortVal;
        setSortBy(id);
        setSortByOrder(desc === true ? 'desc' : 'asc');
      } else {
        setSortBy(undefined);
        setSortByOrder(undefined);
      }
    },
    [getCurrentSortBy]
  );

  const tableState = useBlockTable('adminApplicationErrors');

  const columnWidths = useTableColumnWidths(AdminApplicationErrorsColumns as any, tableState.state.columnOrder);

  return (
    <>
      {applicationErrorsIsLoading && <Loading />}
      <useParentSizeContext.Provider>
        {!applicationErrorsIsLoading && applicationErrors && (
          <>
            <ApplicationErrorsFilters
              search={search}
              startDate={timeFrame.startDate}
              endDate={timeFrame.endDate}
              onSave={onTimeframeChange}
              onSearchSubmit={onSearchSubmit}
              applicationErrors={applicationErrors.errors}
            />
            {hasData && applicationErrors.errors.length >= 1 && (
              <Table
                tableKey="adminApplicationErrors"
                columns={AdminApplicationErrorsColumns}
                numColumns={AdminApplicationErrorsColumns.length}
                columnWidths={columnWidths}
                initialState={initialState}
                data={applicationErrors.errors}
                loaded={hasData}
                onSort={onSort}
                sortable
                getRowKey={getRowKey}
                renderSubRow={renderSubRow}
                manualPagination
                manualSortBy
                onPage={onPage}
                onPageSize={onPageSize}
                pageCount={pageCount}
              />
            )}
          </>
        )}
        {!applicationErrorsIsLoading && applicationErrors.errors.length === 0 && (
          <EmptyBlockContent>No application errors found.</EmptyBlockContent>
        )}
      </useParentSizeContext.Provider>
    </>
  );
};
